'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { refreshSessionUser, useSessionUser } from '@/lib/use-session-user'

/**
 * Google One Tap, offered on every public page.
 *
 * Renders NOTHING. The prompt is Google's own card in the corner of the
 * viewport, so there is no markup here to style — this file exists to decide
 * *when* to ask for it, and to hand the resulting token to the server.
 *
 * ── WHY THIS TALKS TO GOOGLE DIRECTLY ────────────────────────────────────
 * better-auth ships `oneTapClient()`, and it is not used. In 1.6.25 its
 * `getActions` signature does not satisfy `BetterAuthClientPlugin`, and adding
 * it collapses the whole plugin array's inference: `authClient` degrades to the
 * bare `ReactAuthClient`, taking `admin` and `oauth2` with it — the MCP consent
 * screen stops type-checking for a reason that has nothing to do with it.
 * Silencing that with a cast would mean casting again at every call site.
 *
 * What the plugin actually does is ~40 lines of Google Identity Services setup
 * plus a POST, and the part that MATTERS — verifying the id token, applying the
 * hosted-domain rule, minting the session — is the SERVER plugin, which is
 * installed and doing exactly that. So this is the client half, written out.
 *
 * ── WHAT STOPS IT ────────────────────────────────────────────────────────
 * Three things, in order:
 *  - no `clientId`, which means the server holds no Google keys — the prompt
 *    would open onto a sign-in that cannot complete (`GOOGLE_ONE_TAP_CLIENT_ID`);
 *  - a session, which is also why the panel never prompts: it is behind a login,
 *    so the session has already resolved by the time this renders;
 *  - `isPending`, so the first frame of a hard load — where the session has not
 *    come back and every visitor looks anonymous — does not fire a prompt at
 *    somebody who is signed in.
 *
 * Google runs its own cooldown on top: dismissing the card suppresses it for a
 * while. `prompted` keeps this to one attempt per mount, and the mount is a
 * layout that survives navigation, so browsing the site is not a stream of
 * prompts.
 *
 * ── NO REDIRECT ──────────────────────────────────────────────────────────
 * The visitor was reading a page; signing in is not a request to leave it. The
 * callback therefore carries no `callbackURL` — the endpoint sets the session
 * cookie, `router.refresh()` re-renders the server tree in place, and the
 * header's account control becomes their avatar without the page moving.
 */

/** The slice of Google Identity Services this file uses. */
type Gsi = {
  accounts: {
    id: {
      initialize(config: {
        client_id: string
        callback: (response: { credential?: string }) => void
        auto_select?: boolean
        cancel_on_tap_outside?: boolean
        context?: 'signin' | 'signup' | 'use'
        use_fedcm_for_prompt?: boolean
      }): void
      prompt(): void
    }
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client'

/**
 * Google's script, fetched at most once per document.
 *
 * Module-level rather than per-component: the promise is the lock. Nothing on
 * the site loads this at boot — it is requested only when a signed-out visitor
 * is actually going to be prompted, so a signed-in session never pays for it.
 */
let gsi: Promise<Gsi | null> | null = null

function loadGsi(): Promise<Gsi | null> {
  gsi ??= new Promise<Gsi | null>((resolve) => {
    const ready = () => resolve((window as unknown as { google?: Gsi }).google ?? null)
    if ((window as unknown as { google?: Gsi }).google) return ready()

    const el = document.createElement('script')
    el.src = GSI_SRC
    el.async = true
    el.onload = ready
    // A blocked script is a prompt that never appears, not a broken page.
    el.onerror = () => resolve(null)
    document.head.append(el)
  })
  return gsi
}

export function GoogleOneTap({ clientId }: { clientId: string | undefined }) {
  // The same answer the bar reads, from the same one request — and without
  // better-auth's client, which would put `zod` back into the first load of
  // every public page. See `useSessionUser`.
  const { user: session, pending: isPending } = useSessionUser()
  const router = useRouter()
  const prompted = useRef(false)

  useEffect(() => {
    if (!clientId || isPending || session || prompted.current) return
    // Set BEFORE the await, and with no cleanup that undoes it. Strict mode
    // runs this effect twice on mount; a guard that a cleanup resets would let
    // the second pass through, and a guard set after the await would let both
    // passes start. This way the second pass sees the flag and returns.
    prompted.current = true

    void loadGsi().then((google) => {
      if (!google) return
      google.accounts.id.initialize({
        client_id: clientId,
        // An offer, not a gate: nothing is pre-selected for someone with more
        // than one Google account, and tapping outside dismisses it.
        auto_select: false,
        cancel_on_tap_outside: true,
        // `signin`, not `signup` — it produces "Zaloguj się" rather than
        // "Zarejestruj się", which is the honest wording while registration can
        // be paused underneath (the server refuses unknown addresses then).
        context: 'signin',
        use_fedcm_for_prompt: true,
        callback: ({ credential }) => {
          if (!credential) return
          void fetch('/api/auth/one-tap/callback', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ idToken: credential }),
          })
            .then((response) => {
              // A refusal here is a sign-in that did not happen — most likely
              // an address we have never seen while sign-ups are paused. The
              // visitor still has every route the site already offered, and
              // the login page explains that case in Polish.
              if (response.ok) {
                // The cached "nobody is signed in" is now wrong, and the bar
                // reads it: without this the avatar only appears on the next
                // hard load.
                refreshSessionUser()
                router.refresh()
              }
            })
            .catch(() => {})
        },
      })
      google.accounts.id.prompt()
    })
  }, [clientId, isPending, session, router])

  return null
}
